/**
 * @fileoverview PalmPuffin Privacy Policy registry data.
 *
 * Hand-migrated faithfully from
 * `apps/palm-web/src/portals/marketing-website/pages/PrivacyPolicyPage.jsx`.
 */

/** @type {import('../../types.js').LegalDocument} */
export const privacy = Object.freeze({
  appKey: 'palm',
  documentKey: 'privacy',
  frontmatter: {
    title: 'Privacy Policy',
    productName: 'PalmPuffin',
    legalEntity: 'D Town Retail, LLC',
    effectiveDate: '2026-03-01',
    contactEmail: 'privacy@palmpuffin.com',
    canonicalUrl: 'https://palmpuffin.com/privacy',
    ogImage: 'https://palmpuffin.com/og-image.jpg',
    description:
      'PalmPuffin privacy policy. Learn how we collect, use, and protect your scheduling data and personal information in our mobile schedule self-service platform.',
  },
  sections: [
    {
      id: 'information-we-collect',
      title: '1. Information We Collect',
      order: 1,
      blocks: [
        {
          type: 'paragraph',
          text: 'When you use {productName}, we collect information you provide directly, including your name, email address, phone number, and employer information necessary to provide scheduling self-service features.',
        },
        {
          type: 'paragraph',
          text: 'We also automatically collect usage data such as IP addresses, browser type, pages visited, and timestamps to improve our services and ensure security.',
        },
      ],
    },
    {
      id: 'how-we-use-your-information',
      title: '2. How We Use Your Information',
      order: 2,
      blocks: [
        {
          type: 'paragraph',
          text: 'We use your information to provide schedule viewing, availability management, time-off requests, shift pickup, and shift swap services. We also use it to send notifications about schedule changes and request updates.',
        },
        {
          type: 'paragraph',
          text: 'We may also use your information to send service updates, respond to inquiries, improve our platform, and comply with legal obligations.',
        },
      ],
    },
    {
      id: 'data-security',
      title: '3. Data Security',
      order: 3,
      blocks: [
        {
          type: 'paragraph',
          text: 'We implement industry-standard security measures including encryption in transit (TLS) and at rest, access controls, and regular security audits to protect your scheduling data and personal information.',
        },
      ],
    },
    {
      id: 'data-sharing',
      title: '4. Data Sharing',
      order: 4,
      blocks: [
        {
          type: 'paragraph',
          text: 'We do not sell your personal information. We share scheduling data with your employer solely for the purpose of managing workforce schedules. We may also share data with service providers who assist in operating our platform under strict confidentiality agreements.',
        },
      ],
    },
    {
      id: 'data-retention',
      title: '5. Data Retention',
      order: 5,
      blocks: [
        {
          type: 'paragraph',
          text: 'We retain your data for as long as your account is active or as needed to provide services. Schedule and availability data is retained for the duration required by your employer and applicable labor regulations.',
        },
      ],
    },
    {
      id: 'your-rights',
      title: '6. Your Rights',
      order: 6,
      blocks: [
        {
          type: 'paragraph',
          text: 'You have the right to access, correct, or delete your personal information. You may also request a copy of your data or opt out of non-essential communications. To exercise these rights, contact us at the address below.',
        },
      ],
    },
    {
      id: 'cookies-and-analytics',
      title: '7. Cookies and Analytics',
      order: 7,
      blocks: [
        {
          type: 'paragraph',
          text: 'We use cookies and similar technologies to analyze site traffic and improve your experience. You can manage cookie preferences through our cookie consent banner. Analytics data is collected only with your consent.',
        },
      ],
    },
    {
      id: 'changes-to-this-policy',
      title: '8. Changes to This Policy',
      order: 8,
      blocks: [
        {
          type: 'paragraph',
          text: 'We may update this privacy policy from time to time. We will notify you of significant changes by email or through a notice on our website. Continued use of our services after changes constitutes acceptance of the updated policy.',
        },
      ],
    },
    {
      id: 'contact-us',
      title: '9. Contact Us',
      order: 9,
      blocks: [
        {
          type: 'paragraph',
          text: 'If you have questions about this privacy policy or our data practices, please contact us at {contactEmail}.',
        },
        { type: 'contact' },
      ],
    },
  ],
});
